import api from "@/api"
import recurly from "@/services/recurly"
import { trackEvent } from "@/services/tracking/hardened"
import { go, reload } from "@/utils/location"
import { cookie, eraseCookie } from "@/utils/cookie"
import { isEmailInvalid } from "@/modules/email-validation"
import getLocale from "@/utils/locale"
import { STANDALONE_CHECKOUT_ERROR_KEY } from "@/utils/standalone"

export default {
  config: (
    { commit, getters },
    {
      productType,
      plan,
      coupon,
      countryCode,
      detectedCountry,
      detectedRegion,
      tosPath,
      trialDays,
      defaultPaymentMethod,
      countryOptions,
      regionsByCountry,
      isIosAppPurchase,
      isStandalone,
      b2bTrailEnds,
      version = 1
    }
  ) => {
    commit("config", {
      productType,
      countryCode,
      detectedCountry,
      detectedRegion,
      tosPath,
      trialDays,
      countryOptions,
      b2bTrailEnds,
      regionsByCountry,
      isIosAppPurchase
    })
    commit("coupon", coupon)
    commit("isStandalone", isStandalone)

    if (Number(version) === 1) {
      commit("plan", plan)
      let selectedPaymentMethod = getters.paymentMethods[defaultPaymentMethod]
        ? defaultPaymentMethod
        : Object.keys(getters.paymentMethods)[0]

      commit("selectedPaymentMethod", selectedPaymentMethod)
    } else if (Number(version) === 2) {
      if (plan) {
        commit("plan", plan)
        let selectedPaymentMethod = getters.paymentMethods[defaultPaymentMethod]
          ? defaultPaymentMethod
          : Object.keys(getters.paymentMethods)[0]

        commit("selectedPaymentMethod", selectedPaymentMethod)
      }
    } else if (Number(version) === 3) {
      /**
       * Hack: select cc by default for v3 clone
       * Plan data is not available to be able to select
       * default payment method as it is a popup on the plan page.
       * This should be fixed by injecting plan data
       * to the choose plan controller
       */
      commit("defaultPaymentMethod", "cc")
    }
  },

  selectPlan: ({ commit, state }, plan) => {
    commit("plan", plan)
    commit("selectedPaymentMethod", state.defaultPaymentMethod)
  },

  fetchPlan: ({ dispatch, commit, state }) => {
    commit("loading", true)
    return api
      .plans({
        zip: state.zip.value,
        countryCode: state.countryCode,
        code: state.coupon.code,
        sku: state.plan.sku,
        token: state.plan.token,
        productType: state.productType,
        vatNumber: state.vatNumber.value
      })
      .then((data) => {
        commit("loading", false)
        dispatch("saveCoupon", data.coupon)
        commit("tax", data.tax)
        commit("plan", data.plan)
      })
  },

  validate: ({ state, commit }) =>
    new Promise((resolve, reject) => {
      commit("emailError", false)
      commit("zip", {
        ...state.zip,
        error: false
      })

      const errorFields = []

      if (state.isStandalone && isEmailInvalid(state.email.value)) {
        commit("emailError", true)
        errorFields.push("email")
      }

      if (!state.street1 || state.street1.trim() === "") {
        errorFields.push("street1")
      }

      if (!state.city || state.city.trim() === "") {
        errorFields.push("city")
      }

      if (!state.region || state.region.trim() === "") {
        errorFields.push("region")
      }

      if (!state.countryCode || state.countryCode.trim() === "") {
        errorFields.push("countryCode")
      }

      // Apple Pay doesn't require zip validation as we get the billing info from Apple
      if (state.selectedPaymentMethod !== "applePay") {
        if (!state.zip.value || state.zip.value === "") {
          commit("zip", {
            ...state.zip,
            error: true
          })
          errorFields.push("zip")
        }
      }

      if (errorFields.length > 0) {
        return reject({ fields: errorFields })
      }

      resolve()
    }),

  submit: async ({ commit, dispatch, state }, payload) => {
    commit("error", null)

    /* eslint-disable camelcase */
    let params = {
      ...payload,
      subscription_plan_token: state.plan.token,
      payment_type: state.selectedPaymentMethod,
      country: state.countryCode,
      postal_code: state.zip.value,
      street1: state.street1,
      street2: state.street2,
      city: state.city,
      region: state.region,
      vat_number: state.vatNumber.value,
      coupon_code: state.coupon.code,
      currency: state.plan.currency
    }

    if (state.isStandalone) {
      params.email = state.email.value
    }

    return api
      .purchase(params, state.productType)
      .then((data) => {
        if (data.message === "3d_secure_action_required") {
          trackEvent({
            category: "body-20181129-payment_form",
            name: "show",
            action: "3ds"
          })
          return recurly.threeds.challenge(data.token, document.body).then((token) => {
            trackEvent({
              category: "body-20181129-payment_form",
              name: "token",
              action: "3ds"
            })

            let params = {
              ...payload,
              subscription_plan_token: state.plan.token,
              payment_type: state.selectedPaymentMethod,
              country: state.countryCode,
              postal_code: state.zip.value,
              street1: state.street1,
              street2: state.street2,
              city: state.city,
              region: state.region,
              vat_number: state.vatNumber.value,
              coupon_code: state.coupon.code,
              three_ds_token: token.id,
              currency: state.plan.currency
            }

            /* eslint-enable camelcase */
            return api.purchase(
              {
                ...params
              },
              state.productType
            )
          })
        } else {
          return Promise.resolve(data)
        }
      })
      .then((data) => {
        const { successPath } = data
        eraseCookie("coupon_code")
        go(successPath)
      })
      .catch((err) => {
        const message = getErrorMessage(err)

        switch (err.content && err.content.status) {
          case "email_registered": {
            const params = new URLSearchParams({
              // eslint-disable-next-line camelcase
              last_page_before_login: location.pathname + location.search,
              // eslint-disable-next-line camelcase
              from_standalone_checkout: true,
              email: state.email.value
            })
            go(`/${getLocale()}/nc/login?${params.toString()}`)
            return
          }
          case "email_not_valid":
            commit("error", err.content.status)
            break
          case "user_creation_error":
            commit("error", "server error")
            break
          default: {
            const isNewUserCreated = state.isStandalone
            if (isNewUserCreated) {
              // Saving error message to show it after page reload
              sessionStorage.setItem(STANDALONE_CHECKOUT_ERROR_KEY, message)
              // Reload page to update user's session cookie and switch to non-standalone checkout
              reload()
            } else {
              commit("error", message)
            }
          }
        }

        trackEvent({
          category: "body-20181129-payment_form",
          action: "blinkist-error",
          customVariables: {
            Message: message
          }
        })
      })
      .finally(() => {
        dispatch("sending", false)
      })
  },

  submitCoupon: ({ commit, dispatch }, couponCode) => {
    commit("coupon", { code: couponCode, loading: true })
    trackEvent({
      category: "form-20210120-checkout_coupon",
      action: "1-submit",
      name: couponCode
    })
    return dispatch("fetchPlan").finally(() => {
      commit("coupon", { loading: false })
    })
  },

  submitZip: ({ commit, dispatch }) => {
    commit("zip", { loading: true })
    return dispatch("fetchPlan")
      .then(() => {
        commit("zip", { error: false })
      })
      .catch(() => {
        commit("zip", { error: true })
        trackEvent({
          category: "body-20201125-payment_form",
          action: "vat_checkout_summary_error"
        })
      })
      .finally(() => {
        commit("zip", { loading: false })
      })
  },

  submitVatNumber: ({ commit, dispatch }) => {
    return dispatch("fetchPlan")
      .then(() => {
        commit("vatNumber", { error: false })
      })
      .catch(() => {
        commit("vatNumber", { error: true })
        trackEvent({
          category: "body-20201125-payment_form",
          action: "vat_checkout_summary_error"
        })
      })
      .finally(() => {
        commit("vatNumber", { loading: false })
      })
  },

  country: ({ commit, dispatch }, countryCode) => {
    commit("countryCode", countryCode)
    trackEvent({
      category: "body-20210219-payment_form",
      action: "change_country",
      name: countryCode
    })

    commit("zip", { value: null })
    dispatch("submitZip")

    commit("region", null)
  },

  saveCoupon: ({ commit, state }, payload) => {
    if (!payload) {
      commit("coupon", {
        type: null,
        value: null,
        code: null,
        isSingleUse: true,
        error: state.coupon.loading
      })
      return
    }
    commit("coupon", payload)
    cookie("coupon_code", payload.code, 1)
  },

  resetCoupon: ({ commit, dispatch }) => {
    commit("coupon", {
      code: null,
      type: null,
      value: null,
      error: null,
      isSingleUse: true
    })
    eraseCookie("coupon_code")
    dispatch("fetchPlan")
  },

  sending: ({ commit }, payload) => {
    commit("loading", payload)
    commit("sending", payload)
  },

  selectedPaymentMethod: ({ commit }, payload) => {
    commit("selectedPaymentMethod", payload)
    trackEvent({
      category: "body-20181129-payment_form",
      name: "select",
      action: payload
    })
  },

  updateGiftcardReceiver: ({ commit }, payload) => {
    commit("giftcard", payload)
  },

  updateGiftcardMessage: ({ commit }, payload) => {
    commit("giftcard", payload)
  }
}

const getErrorMessage = (err) => {
  if (err.code === "3ds-auth-error") {
    return err.code
  }
  return (err.content && err.content.message) || err.message
}
